import React from 'react';
import { MapPin, Globe, X } from 'lucide-react';

/**
 * LocationPrompt - Beautiful UI to ask user for location permission
 * 
 * Shows before triggering browser's native location permission
 * Explains WHY we need location in user-friendly way
 */
const LocationPrompt = ({ 
  onAllow, 
  onDecline, 
  onClose,
  isVisible = false,
  currentCurrency = 'USD'
}) => {
  if (!isVisible) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      style={{ backdropFilter: 'blur(4px)' }}
    >
      <div 
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative animate-fadeIn"
        style={{ animation: 'slideUp 0.3s ease-out' }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="bg-blue-100 rounded-full p-4">
            <MapPin className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-gray-900 text-center mb-3">
          Enable Location for Better Pricing
        </h2>

        {/* Description */}
        <p className="text-gray-600 text-center mb-6 leading-relaxed">
          We'll show you prices in your local currency for a better experience. 
          Your location is only used for currency conversion and is never stored or shared.
        </p>

        {/* Benefits List */}
        <div className="bg-blue-50 rounded-lg p-4 mb-6 space-y-3">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-1">
              <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center">
                <span className="text-white text-xs">✓</span>
              </div>
            </div>
            <div>
              <p className="text-sm text-gray-700 font-medium">See prices in your currency</p>
              <p className="text-xs text-gray-500">Currently showing {currentCurrency}</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-1">
              <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center">
                <span className="text-white text-xs">✓</span>
              </div>
            </div>
            <div>
              <p className="text-sm text-gray-700 font-medium">Accurate cost estimates</p>
              <p className="text-xs text-gray-500">No surprises at checkout</p>
            </div>
          </div>
          
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-1">
              <div className="w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center">
                <span className="text-white text-xs">✓</span>
              </div>
            </div>
            <div>
              <p className="text-sm text-gray-700 font-medium">Privacy protected</p>
              <p className="text-xs text-gray-500">Never stored or shared</p>
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex gap-3">
          <button
            onClick={onDecline}
            className="flex-1 px-6 py-3 rounded-lg border-2 border-gray-200 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
          >
            Maybe Later
          </button>
          <button
            onClick={onAllow}
            className="flex-1 px-6 py-3 rounded-lg bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors shadow-lg"
          >
            Allow Location
          </button>
        </div>

        {/* Small disclaimer */}
        <p className="text-xs text-gray-400 text-center mt-4">
          You can change this anytime in your browser settings
        </p>
      </div>

      <style jsx>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
};

/**
 * LocationIndicator - Small badge showing current location/currency
 * Shows at top of pricing page
 */
export const LocationIndicator = ({ 
  locationInfo, 
  currency, 
  onChangeLocation,
  isLoading = false 
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-600">
        <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
        <span>Detecting location...</span>
      </div>
    );
  }

  if (!locationInfo) {
    return (
      <div className="flex items-center gap-2">
        <button
          onClick={onChangeLocation}
          className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
        >
          <Globe className="w-4 h-4" />
          <span>Enable Location for Local Prices</span>
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 text-sm">
      <div className="flex items-center gap-2 text-gray-600">
        <MapPin className="w-4 h-4" />
        <span>{locationInfo.city}, {locationInfo.country}</span>
      </div>
      <div className="h-4 w-px bg-gray-300"></div>
      <div className="flex items-center gap-2">
        <span className="font-semibold text-gray-700">
          {currency}
        </span>
        <button
          onClick={onChangeLocation}
          className="text-xs text-blue-600 hover:text-blue-700 underline"
        >
          Change
        </button>
      </div>
    </div>
  );
};

/**
 * PermissionDeniedMessage - Shows when user blocks location
 * Gentle message with option to continue with USD
 */
export const PermissionDeniedMessage = ({ onContinue, onRetry }) => {
  return (
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0">
          <MapPin className="w-5 h-5 text-yellow-600" />
        </div>
        <div className="flex-1">
          <h4 className="text-sm font-semibold text-yellow-900 mb-1">
            Location Access Blocked
          </h4>
          <p className="text-sm text-yellow-700 mb-3">
            No worries! Prices will be shown in USD. You can enable location later from your browser settings for local currency.
          </p>
          <div className="flex gap-2">
            <button
              onClick={onContinue}
              className="px-4 py-2 text-sm bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
            >
              Continue with USD
            </button>
            <button
              onClick={onRetry}
              className="px-4 py-2 text-sm border border-yellow-600 text-yellow-700 rounded-lg hover:bg-yellow-50 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationPrompt;
